<template>
  <div class="operator-list">
    <div class="operator-list__bar">
      <span class="operator-list__title">{{ text.operatorListTitle }}</span>
      <el-button v-if="ability.add" size="mini" type="primary" plain icon="el-icon-plus" @click="$emit('add')">{{
        text.operatorAddAction
      }}</el-button>
    </div>

    <el-alert v-if="denied" :title="denied" type="info" :closable="false" show-icon />

    <el-table
      v-else
      class="operator-list__table"
      :data="operators"
      size="mini"
      border
      highlight-current-row
      v-loading="loading"
      :empty-text="text.operatorEmpty"
      :row-class-name="rowClass"
      @current-change="onPick">
      <el-table-column :label="text.operatorLabel" min-width="150">
        <template slot-scope="scope">
          <span class="operator-list__name">{{ nameOf(scope.row) }}</span>
          <el-tag v-if="scope.row.hqYn === 'Y'" size="mini" type="warning">{{ text.hqTag }}</el-tag>
          <el-tag v-if="scope.row.useYn === 'N'" size="mini" type="info">{{ text.inactiveTag }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="text.agencyCountColumn" width="70" align="right">
        <template slot-scope="scope">{{ scope.row.agencyCount != null ? scope.row.agencyCount : '-' }}</template>
      </el-table-column>
      <el-table-column :label="text.storeCountColumn" width="70" align="right">
        <template slot-scope="scope">{{ scope.row.storeCount != null ? scope.row.storeCount : '-' }}</template>
      </el-table-column>
      <el-table-column :label="$t('관리')" width="120">
        <template slot-scope="scope">
          <el-button v-if="buttonsOf(scope.row).edit" size="mini" type="text" @click.stop="$emit('edit', scope.row)">{{
            text.editAction
          }}</el-button>
          <el-button
            v-if="buttonsOf(scope.row).off"
            size="mini"
            type="text"
            @click.stop="$emit('deactivate', scope.row)"
            >{{ text.operatorOffAction }}</el-button
          >
          <el-button
            v-if="buttonsOf(scope.row).on"
            size="mini"
            type="text"
            @click.stop="$emit('reactivate', scope.row)"
            >{{ text.operatorOnAction }}</el-button
          >
          <el-button
            v-if="buttonsOf(scope.row).waive"
            size="mini"
            type="text"
            @click.stop="$emit('waive', scope.row)"
            >{{ text.operatorWaiveAction }}</el-button
          >
        </template>
      </el-table-column>
    </el-table>

    <p class="operator-list__hint">{{ text.operatorPickHint }}</p>
  </div>
</template>

<script>
import { isInactiveOrg, operatorRowButtons, orgLabel } from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'

/**
 * 운영사 목록 — 화면 #1 의 왼쪽 판.
 *
 * **버튼 자격은 부모가 준다**([ability], `callpopOperatorsMixin.operatorAbility`) — 세션 액션(`sa_actions`)과
 * 서버 능력 값(§2-2)의 곱이다. 역할 문자열을 보지 않는 이유는 서버가 같은 표를 이미 갖고 있기 때문이고
 * (SQL `023` §6), 리소스 없는 호출은 403 이 아니라 **401** 이라 그 클릭 한 번이 세션을 끝낸다(서버 보고서 §10 3).
 *
 * 쓰기는 부모(다이얼로그·확인창)가 한다 — 이 판은 고르고 알릴 뿐이다.
 */
export default {
  name: 'OperatorList',

  props: {
    operators: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** 못 읽은 이유 — 있으면 표 대신 그 문장을 보인다 */
    denied: { type: String, default: '' },
    /** 쓰기 자격 `{add, up, off, on, waive, hq}` — 없으면 아무 버튼도 서지 않는다 */
    ability: { type: Object, default: () => ({}) },
  },

  data() {
    return { text: CALLPOP_TEXT }
  },

  methods: {
    /** 행마다의 버튼 — 자격에 그 행의 상태(총괄·비활성)를 건다(판정은 `callpopOrg`) */
    buttonsOf(row) {
      return operatorRowButtons(row, this.ability)
    },
    nameOf(row) {
      return orgLabel(row && row.operatorNm, row && row.code)
    },
    onPick(row) {
      if (row) this.$emit('pick', row)
    },
    /** 비활성 행은 흐리게 — 「비활성 포함」을 켰을 때만 온다(R-A). 태그(「비활성」)는 이름 칸이 따로 단다 */
    rowClass({ row }) {
      return isInactiveOrg(row) ? 'operator-list__row--inactive' : ''
    },
  },
}
</script>

<style scoped>
.operator-list__bar {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}
.operator-list__title {
  font-size: 14px;
  font-weight: 600;
}
.operator-list__name {
  margin-right: 4px;
}
.operator-list__hint {
  color: #909399;
  font-size: 12px;
  margin: 6px 0 0;
}
</style>

<style>
/* 행 클래스는 el-table 안쪽 요소라 scoped 로 닿지 않는다(엑셀 다이얼로그의 오류 행과 같은 방식) */
.operator-list__row--inactive td {
  color: #c0c4cc;
}
</style>
